// Procesamiento de la cola (§10): una sola pestaña sube a la vez (Web Locks); cada archivo sigue
// reservar → finalizar previo → subir → finalizar → limpiar. La evidencia válida es "ready" en servidor.
import { fetchMyProfile } from '../data/accounts.ts';
import { toAppError } from '../data/errors.ts';
import { BUCKET, finalizeMedia, reserveMedia, type MediaFile } from '../data/media.ts';
import { supabase } from '../data/supabase.ts';
import { purgeOrderLocalData } from '../local/cleanup.ts';
import { listQueue, removeQueue, replaceQueueId, updateQueue, type QueueRecord } from './queue.ts';
import { setBusy } from '../pwa/store.ts';
import { releaseWakeLock, requestWakeLock } from './wake-lock.ts';

type Outcome = 'confirmed' | 'failed' | 'offline' | 'skipped' | 'purged';

const DAMAGED = 'El archivo guardado en este dispositivo está dañado o incompleto. Vuelve a seleccionarlo.';
let running = false;

export type ProgressListener = (mediaId: string, fraction: number) => void;
const progressListeners = new Set<ProgressListener>();
export function onUploadProgress(listener: ProgressListener): () => void {
  progressListeners.add(listener);
  return () => progressListeners.delete(listener);
}

/** Procesa la cola del usuario. Seguro de llamar varias veces y desde varias pestañas. */
export async function processQueue(userId: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    await navigator.locks.request(`servicioos-upload-${userId}`, { ifAvailable: true }, async (lock) => {
      if (lock) await loop(userId);
    });
  } else if (!running) {
    running = true;
    try {
      await loop(userId);
    } finally {
      running = false;
    }
  }
}

async function loop(userId: string): Promise<void> {
  const items = (await listQueue(userId)).filter((i) => !i.permanent);
  if (items.length === 0) return;
  await requestWakeLock();
  setBusy('uploads', true);
  try {
    for (const item of items) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) break;
      if (item.phase === 'confirmed') {
        await removeQueue(item.id).catch(() => undefined);
        continue;
      }
      const outcome = await uploadOne(item);
      if (outcome === 'offline') break;
    }
  } finally {
    setBusy('uploads', false);
    releaseWakeLock();
  }
}

async function confirmLocally(item: QueueRecord): Promise<Outcome> {
  try {
    await removeQueue(item.id);
  } catch {
    // La carga está confirmada; solo falló la limpieza local. Se purga en otra pasada.
    await updateQueue(item.id, { phase: 'confirmed', error: null }).catch(() => undefined);
  }
  return 'confirmed';
}

async function handleAccessError(item: QueueRecord): Promise<Outcome> {
  try {
    await fetchMyProfile(); // sesión y cuenta vigentes → la orden ya no es accesible
  } catch {
    return 'offline';
  }
  await purgeOrderLocalData(item.userId, item.orderId).catch(() => undefined);
  return 'purged';
}

async function uploadOne(original: QueueRecord, retriedIdentity = false): Promise<Outcome> {
  let item = original;
  const { data } = await supabase.auth.getSession();
  if (data.session?.user.id !== item.userId) return 'skipped';

  if (!(item.blob instanceof Blob) || item.bytes <= 0 || item.blob.size !== item.bytes) {
    await updateQueue(item.id, { phase: 'failed', permanent: true, error: DAMAGED });
    return 'failed';
  }

  let reservation: MediaFile;
  try {
    reservation = await reserveMedia({
      orderId: item.orderId,
      mediaId: item.id,
      kind: item.kind,
      bytes: item.bytes,
      duration: item.duration,
    });
  } catch (error) {
    const appError = toAppError(error);
    if (appError.code === 'media_conflict' && !retriedIdentity) {
      // Reserva expirada/eliminada o identidad reutilizada: nueva identidad, mismo Blob.
      const next = await replaceQueueId(item.id, crypto.randomUUID());
      return next ? uploadOne(next, true) : 'failed';
    }
    if (appError.code === 'permission' || appError.code === 'not_found') return handleAccessError(item);
    if (appError.code === 'network') return 'offline';
    const permanent = appError.code === 'order_locked' || appError.code === 'validation';
    await updateQueue(item.id, { phase: 'failed', permanent, error: appError.message, attempts: item.attempts + 1 });
    return 'failed';
  }

  item = { ...item, phase: 'reserved' };
  await updateQueue(item.id, { phase: 'reserved', error: null });
  if (reservation.status === 'ready') return confirmLocally(item);

  // Un intento anterior pudo haber subido el objeto: se intenta confirmar antes de subir otra vez.
  try {
    await finalizeMedia(item.id);
    return confirmLocally(item);
  } catch (error) {
    if (toAppError(error).code !== 'not_confirmed') return recordFailure(item, error);
  }

  try {
    if (item.kind === 'image') {
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(reservation.path, item.blob, { contentType: 'image/jpeg', upsert: false });
      if (error && !/exist|duplicate/i.test(error.message)) throw error;
    } else {
      const { uploadResumable } = await import('./tus.ts');
      await uploadResumable({
        blob: item.blob,
        path: reservation.path,
        userId: item.userId,
        mediaId: item.id,
        onProgress: (fraction) => progressListeners.forEach((l) => l(item.id, fraction)),
      });
    }
    await finalizeMedia(item.id);
    return confirmLocally(item);
  } catch (error) {
    return recordFailure(item, error);
  }
}

async function recordFailure(item: QueueRecord, error: unknown): Promise<Outcome> {
  const appError = toAppError(error);
  const offline = appError.code === 'network' || (typeof navigator !== 'undefined' && !navigator.onLine);
  if (appError.code === 'permission') return handleAccessError(item);
  await updateQueue(item.id, {
    phase: 'failed',
    permanent: appError.code === 'media_conflict',
    error: offline ? 'Sin conexión: se reintentará al volver la señal.' : appError.message,
    attempts: item.attempts + 1,
  });
  return offline ? 'offline' : 'failed';
}
